import { useQueryClient } from "@tanstack/react-query";
import { Skeleton } from "@veil/ui/components/skeleton";
import CheckCircle2 from "lucide-react/dist/esm/icons/check-circle-2";
import { startTransition, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, unwrap } from "@/lib/api";
import { ApplyLogView, type ApplyStatus } from "./apply-log-view";
import { CodeView } from "./code-view";
import { DiffView } from "./diff-view";
import type { PreviewResponse } from "./types";

export type RenderPhase = "preview" | "applying" | "done";
export type RenderView = "diff" | "current" | "next";

export interface UseRenderResult {
	phase: RenderPhase;
	preview: PreviewResponse | null;
	previewLoading: boolean;
	previewErr: string | null;
	applyLog: string;
	logVersion: number;
	applyStatus: ApplyStatus;
	operationId: string | null;
	isClean: boolean;
	canApply: boolean;
	startApply: () => Promise<void>;
	retry: () => void;
}

/**
 * Hook owning the entire Render & Reload state machine: loads the preview
 * when active, fires the apply mutation, and SSE-streams the operation log
 * until the server emits `end`. Lives in this module so both the legacy
 * inline tab and the new drawer can drive the same flow.
 */
export function useRender({
	active,
	canApply,
}: {
	active: boolean;
	canApply: boolean;
}): UseRenderResult {
	const queryClient = useQueryClient();
	const [phase, setPhase] = useState<RenderPhase>("preview");
	const [preview, setPreview] = useState<PreviewResponse | null>(null);
	const [previewErr, setPreviewErr] = useState<string | null>(null);
	const [previewLoading, setPreviewLoading] = useState(false);
	const [applyLog, setApplyLog] = useState("");
	const [logVersion, setLogVersion] = useState(0);
	const [applyStatus, setApplyStatus] = useState<ApplyStatus>(null);
	const [operationId, setOperationId] = useState<string | null>(null);

	// Refresh the preview every time the consumer becomes active so the diff
	// reflects the latest DB state without the user reopening anything.
	useEffect(() => {
		if (!active || phase !== "preview") return;
		let cancelled = false;
		setPreviewLoading(true);
		setPreviewErr(null);
		(unwrap(api.api.cluster.render.preview.post()) as Promise<PreviewResponse>)
			.then((res) => {
				if (!cancelled) setPreview(res);
			})
			.catch((err: unknown) => {
				if (!cancelled) {
					setPreviewErr(err instanceof Error ? err.message : String(err));
				}
			})
			.finally(() => {
				if (!cancelled) setPreviewLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [active, phase]);

	useEffect(() => {
		if (!operationId) return;
		const es = new EventSource(`/api/operations/${operationId}/log/stream`);
		es.addEventListener("log", (e) => {
			const chunk = (e as MessageEvent).data as string;
			// SSE chunks can be chatty; wrap state writes in a transition so
			// the append doesn't pin the main thread while the log scrolls.
			startTransition(() => {
				setApplyLog((prev) => prev + chunk);
				setLogVersion((v) => v + 1);
			});
		});
		es.addEventListener("status", (e) => {
			try {
				const payload = JSON.parse((e as MessageEvent).data) as {
					status: string;
					exitCode: number | null;
				};
				setApplyStatus(payload.status as "succeeded" | "failed");
			} catch {}
		});
		es.addEventListener("end", () => {
			setPhase("done");
			es.close();
			queryClient.invalidateQueries({ queryKey: ["cluster"] });
		});
		es.onerror = () => {
			es.close();
		};
		return () => es.close();
	}, [operationId, queryClient]);

	const startApply = useCallback(async () => {
		setPhase("applying");
		setApplyStatus("running");
		setApplyLog("");
		setLogVersion(0);
		try {
			const res = (await unwrap(api.api.cluster.render.apply.post())) as {
				operationId: string;
			};
			setOperationId(res.operationId);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Apply failed");
			setApplyStatus("failed");
			setPhase("done");
		}
	}, []);

	const retry = useCallback(() => {
		setPhase("preview");
		setApplyStatus(null);
		setApplyLog("");
		setLogVersion(0);
		setOperationId(null);
	}, []);

	const isClean = preview !== null && preview.diff === "";

	return {
		phase,
		preview,
		previewLoading,
		previewErr,
		applyLog,
		logVersion,
		applyStatus,
		operationId,
		isClean,
		canApply,
		startApply,
		retry,
	};
}

const PREVIEW_LOADING_SKELETON = (
	<Skeleton className="h-64 w-full rounded-lg" />
);

/**
 * Pure view that renders the right body for the current render phase. Stays
 * chrome-free so it can sit inside a Sheet, Dialog, or page section. During
 * the preview phase the consumer chooses which slice of the preview payload
 * to show via `view` — diff, current live config, or the would-be next.
 */
export function RenderBody({
	render,
	view,
}: {
	render: UseRenderResult;
	view: RenderView;
}) {
	if (render.phase === "preview") {
		if (render.previewLoading) return PREVIEW_LOADING_SKELETON;
		if (render.previewErr) {
			return (
				<div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm leading-relaxed text-destructive">
					{render.previewErr}
				</div>
			);
		}
		if (!render.preview) return null;

		if (view === "current") {
			return (
				<CodeView
					code={render.preview.current}
					emptyLabel="No live nginx.conf yet — never rendered."
				/>
			);
		}
		if (view === "next") {
			return <CodeView code={render.preview.next} />;
		}
		if (render.isClean) {
			return (
				<div className="flex items-center gap-3 rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4 text-sm leading-relaxed">
					<CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
					Live nginx.conf is already in sync with the database. Switch to
					Current to inspect it, or to Next to compare.
				</div>
			);
		}
		return <DiffView diff={render.preview.diff} />;
	}
	return (
		<ApplyLogView
			log={render.applyLog}
			status={render.applyStatus}
			logVersion={render.logVersion}
		/>
	);
}
